/**
 * Dictionary Generator Button
 *
 * A button that triggers the generation of output variable/meter dictionaries
 * by running a quick sizing-only simulation.
 */

import { useState, useCallback } from 'react';
import { useDictionaryGeneration } from '../stores/useDictionaryGeneration';
import { useIDFStore } from '../stores/idfStore';

interface DictionaryGeneratorButtonProps {
  /** Compact mode for inline display */
  compact?: boolean;
  /** Called when generation completes successfully */
  onComplete?: () => void;
}

/**
 * Button to generate the output variable dictionary
 */
export function DictionaryGeneratorButton({
  compact = false,
  onComplete,
}: DictionaryGeneratorButtonProps) {
  const { generateDictionary, isGenerating, variableCount, meterCount } =
    useDictionaryGeneration();
  const serialize = useIDFStore((state) => state.serialize);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = useCallback(async () => {
    setError(null);
    setShowSuccess(false);

    // Get the current IDF content
    const idfContent = serialize();
    if (!idfContent) {
      setError('No IDF content available');
      return;
    }

    const result = await generateDictionary(idfContent);

    if (result.success) {
      setShowSuccess(true);
      onComplete?.();
      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
    } else {
      setError(result.error ?? 'Failed to generate dictionary');
    }
  }, [generateDictionary, serialize, onComplete]);

  if (compact) {
    return (
      <button
        onClick={handleClick}
        disabled={isGenerating}
        className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
          isGenerating
            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
        }`}
        title="Run a quick simulation to get accurate output variables for your model"
      >
        {isGenerating ? (
          <>
            <LoadingSpinner className="h-3 w-3" />
            <span>Generating...</span>
          </>
        ) : (
          <>
            <RefreshIcon className="h-3 w-3" />
            <span>Get Model Variables</span>
          </>
        )}
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <button
        onClick={handleClick}
        disabled={isGenerating}
        className={`flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
          isGenerating
            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {isGenerating ? (
          <>
            <LoadingSpinner className="h-4 w-4" />
            <span>Generating Dictionary...</span>
          </>
        ) : (
          <>
            <RefreshIcon className="h-4 w-4" />
            <span>Generate Output Variable Dictionary</span>
          </>
        )}
      </button>

      {isGenerating && (
        <p className="text-xs text-gray-500">
          Running a quick sizing simulation to discover all available output variables
          and meters for your model...
        </p>
      )}

      {showSuccess && (
        <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          <CheckIcon className="h-4 w-4" />
          <span>
            Found {variableCount} variables and {meterCount} meters
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <ErrorIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// Icon components
function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
    </svg>
  );
}
